import {
  INV_AVAILABILITY_BLOCKED_LABEL,
  INV_AVAILABILITY_IN_REQUEST_LABEL,
  INV_AVAILABILITY_WRITE_OFF_LABEL,
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
} from '../../constants/inventory-status-labels';
import { InventoryItem } from '../../types/inventory';
import { registrationIsoFromWarehouseListItem } from '../inventory-registration-date';
import { normalizeTags } from '../helpers';

export type WarehouseInventoryUiLabelVariant = 'warehouse' | 'general';

const PLACEMENT_IN_TRANSIT_LABEL: Record<WarehouseInventoryUiLabelVariant, string> = {
  warehouse: 'En Transito',
  general: 'En tránsito',
};

const AVAILABILITY_PARTIAL_PAYMENT_LABEL: Record<WarehouseInventoryUiLabelVariant, string> = {
  warehouse: 'Pago Pendiente',
  general: 'Pago parcial',
};

function mapPlacementStatus(
  apiStatus: string | undefined,
  labelVariant: WarehouseInventoryUiLabelVariant,
): string {
  if (apiStatus === 'ASSIGNED') {
    return INV_STATUS_BRANCH_LABEL;
  }
  if (apiStatus === 'IN_TRANSIT') {
    return PLACEMENT_IN_TRANSIT_LABEL[labelVariant];
  }
  return INV_STATUS_WAREHOUSE_LABEL;
}

function mapAvailabilityStatus(
  apiStatus: string | undefined,
  labelVariant: WarehouseInventoryUiLabelVariant,
): string {
  switch (apiStatus) {
    case 'AVAILABLE':
      return 'Disponible';
    case 'SOLD':
      return 'Vendido';
    case 'RESERVED':
      return INV_AVAILABILITY_BLOCKED_LABEL;
    case 'PARTIAL_PAYMENT':
      return AVAILABILITY_PARTIAL_PAYMENT_LABEL[labelVariant];
    case 'CANCELLED':
      return INV_AVAILABILITY_WRITE_OFF_LABEL;
    case 'IN_REQUEST':
      return INV_AVAILABILITY_IN_REQUEST_LABEL;
    default:
      return 'N/A';
  }
}

function mapTransfer(item: {
  transfer?: {
    id: number;
    transferCode: string;
    createdAt: string;
    status: string;
    completedAt?: string | null;
    cancelledAt?: string | null;
  };
}) {
  if (item.transfer == null) {
    return undefined;
  }
  return {
    id: item.transfer.id,
    transferCode: item.transfer.transferCode,
    createdAt: item.transfer.createdAt,
    status: item.transfer.status,
    completedAt: item.transfer.completedAt ?? null,
    cancelledAt: item.transfer.cancelledAt ?? null,
  };
}

export interface MapWarehouseInventoryProductOptions {
  labelVariant?: WarehouseInventoryUiLabelVariant;
  /** When false, passes API transfer object through unchanged (warehouse query page). */
  mapTransferFields?: boolean;
}

/** Maps a warehouse-inventory-products API row to the UI InventoryItem shape. */
export function mapWarehouseInventoryProductToInventoryItem(
  item: Record<string, unknown>,
  options: MapWarehouseInventoryProductOptions = {},
): InventoryItem {
  const labelVariant = options.labelVariant ?? 'warehouse';
  const mapTransferFields = options.mapTransferFields ?? true;

  const product = item.product as Record<string, unknown> | undefined;
  const category = product?.category as { name?: string } | undefined;
  const record = (item.inventoryRecordItem as { record?: { recordCode?: string } } | undefined)
    ?.record;
  const recordCode =
    record?.recordCode != null && record.recordCode !== '' ? record.recordCode : '—';

  const supplier = item.supplier as { id: number; name: string; status?: string } | undefined;

  return {
    id: (item.productCodeUnique as string) ?? String(item.id),
    originalId: item.id as number,
    name: (product?.name as string) ?? 'N/A',
    type: category?.name ?? 'N/A',
    createdAt:
      registrationIsoFromWarehouseListItem(
        item as Parameters<typeof registrationIsoFromWarehouseListItem>[0],
      ) ?? (item.createdAt as string),
    firstAssignedAt: item.firstAssignedAt as string | undefined,
    transferredAt: item.transferredAt as string | undefined,
    receivedAt: item.receivedAt as string | undefined,
    recordCode,
    status: mapPlacementStatus(item.status as string | undefined, labelVariant),
    originalStatus: item.status as string | undefined,
    transferredToBranch: item.transferredToBranch as boolean | undefined,
    locationType: (item.location as { type?: 'WAREHOUSE' | 'BRANCH' } | undefined)?.type,
    availabilityStatus: mapAvailabilityStatus(
      item.availabilityStatus as string | undefined,
      labelVariant,
    ),
    updatedAt: item.updatedAt as string,
    location: (item.location as { name?: string } | undefined)?.name ?? 'N/A',
    material:
      (item.material as { name?: string } | undefined)?.name ??
      (product?.material as { name?: string } | undefined)?.name ??
      'N/A',
    price: Number(item.salePrice) || Number(product?.salePrice) || 0,
    locationName: (item.location as { name?: string } | undefined)?.name ?? 'N/A',
    locationId: (item.location as { id?: number } | undefined)?.id,
    tags: normalizeTags(product?.tags as Parameters<typeof normalizeTags>[0]),
    condition: item.condition as string | undefined,
    itemNotes: item.itemNotes as string | undefined,
    propertiesDescription: item.propertiesDescription as string | undefined,
    weightGrams: item.weightGrams as number | undefined,
    lengthMm: item.lengthMm as number | undefined,
    diameterSize: item.diameterSize as string | undefined,
    color: item.color as string | undefined,
    workshopCode: item.workshopCode as string | undefined,
    costPrice: Number(item.costPrice) || Number(product?.costPrice) || 0,
    supplier:
      supplier != null
        ? {
            id: supplier.id,
            name: supplier.name,
            status: supplier.status,
          }
        : undefined,
    transfer: mapTransferFields
      ? mapTransfer(item as Parameters<typeof mapTransfer>[0])
      : (item.transfer as InventoryItem['transfer']),
    product: {
      id: (product?.id as number) ?? 0,
      name: (product?.name as string) ?? 'N/A',
      code: (product?.code as string) ?? 'N/A',
      description: (product?.description as string) ?? '',
      price: (product?.price as string) ?? '0',
      imagePath: (product?.imagePath as string) ?? '',
      thumbnailUrl: (product?.thumbnailUrl as string | null) ?? null,
      categoryId: (product?.categoryId as number) ?? 0,
      createdAt: (product?.createdAt as string) ?? '',
      updatedAt: (product?.updatedAt as string) ?? '',
    },
  };
}

/** @deprecated Use mapWarehouseInventoryProductToInventoryItem */
export const mapWarehouseInventoryProductApiToInventoryItem =
  mapWarehouseInventoryProductToInventoryItem;

export function mapWarehouseInventoryProductsToInventoryItems(
  items: Record<string, unknown>[] | undefined,
  options?: MapWarehouseInventoryProductOptions,
): InventoryItem[] {
  if (items == null) {
    return [];
  }
  return items.map((item) => mapWarehouseInventoryProductToInventoryItem(item, options));
}
